import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';
import {
  SimulationClientDto,
  SimulationRequestDto,
  SimulationResultDto,
  SimulationPagedResultDto
} from '../../types/simulation.types';

export class SimulationService extends BaseApiService {
  private readonly basePath = '/v1/simulation';

  /**
   * Lista clientes WhatsApp para simulação com paginação
   */
  async getClients(
    page: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    statusFluxo?: string
  ): Promise<SimulationPagedResultDto> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (searchTerm) params.append('searchTerm', searchTerm);
      if (statusFluxo) params.append('statusFluxo', statusFluxo);

      const response = await this.api.get<SimulationPagedResultDto | ApiResponse<SimulationPagedResultDto>>(
        `${this.basePath}/clients?${params.toString()}`
      );

      // O backend retorna o objeto paginado diretamente ou pode estar encapsulado
      if (response.data && typeof response.data === 'object' && 'items' in response.data) {
        return response.data as SimulationPagedResultDto;
      }

      // Se estiver encapsulado em ApiResponse
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return (response.data as ApiResponse<SimulationPagedResultDto>).data || {
          items: [],
          totalCount: 0,
          pageNumber: 1,
          pageSize: pageSize,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false
        };
      }

      return {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: pageSize,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
      };
    } catch (error: any) {
      console.error('Erro ao buscar clientes:', error);
      throw new Error(error.response?.data?.message || 'Erro ao buscar clientes');
    }
  }

  /**
   * Consulta cliente no Google Sheets via webhook do n8n
   * Retorna se o cliente foi encontrado na base do Sheets
   */
  async consultaClienteNoSheets(cnpj: string): Promise<{ encontrado: boolean; dados?: any }> {
    try {
      // Normalizar CNPJ (remover caracteres não numéricos)
      const cnpjNormalizado = cnpj.replace(/\D/g, '');
      
      if (cnpjNormalizado.length !== 14) {
        throw new Error('CNPJ inválido');
      }

      // URL do webhook do n8n
      const n8nUrl = process.env.REACT_APP_N8N_URL || 'https://n8n.gettel.cloud';
      const webhookUrl = `${n8nUrl}/webhook/consulta-cliente-cnpj`;

      // Fazer requisição POST para o webhook do n8n
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cnpj: cnpjNormalizado }),
      });

      if (!response.ok) {
        // Se o n8n retornar erro, considerar como não encontrado
        throw new Error('Erro ao consultar cliente na base');
      }

      const data = await response.json();

      // Verificar estrutura da resposta
      if (data && typeof data === 'object' && 'encontrado' in data) {
        if (!data.encontrado) {
          throw new Error(data.mensagem || 'Cliente não encontrado na base');
        }
        return {
          encontrado: data.encontrado,
          dados: data.dados
        };
      }

      // Se a resposta não tiver a estrutura esperada, considerar como não encontrado
      throw new Error('Cliente não encontrado na base');
    } catch (error: any) {
      console.error('Erro ao consultar cliente no Sheets:', error);
      // Se for erro de conexão/timeout, lançar erro genérico
      if (error.message && error.message.includes('Cliente não encontrado')) {
        throw error;
      }
      throw new Error('Erro ao consultar cliente na base. Tente novamente.');
    }
  }

  /**
   * Busca cliente por CNPJ ou Razão Social
   */
  async searchClientByCnpjOrName(query: string): Promise<SimulationClientDto> {
    try {
      const response = await this.api.get<SimulationClientDto | ApiResponse<SimulationClientDto>>(
        `${this.basePath}/clients/search?query=${encodeURIComponent(query)}`
      );
      
      // O backend retorna o objeto diretamente ou pode estar encapsulado
      if (response.data && typeof response.data === 'object' && 'id' in response.data && !('success' in response.data)) {
        return response.data as SimulationClientDto;
      }
      
      // Se estiver encapsulado em ApiResponse
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as ApiResponse<SimulationClientDto>).data;
        if (!data) {
          throw new Error('Cliente não encontrado');
        }
        return data;
      }
      
      throw new Error('Cliente não encontrado');
    } catch (error: any) {
      console.error('Erro ao buscar cliente:', error);
      throw new Error(error.response?.data?.message || error.message || 'Cliente não encontrado');
    }
  }

  /**
   * Obtém detalhes de um cliente específico com todas as linhas
   */
  async getClientDetails(clientId: string): Promise<SimulationClientDto> {
    try {
      const response = await this.api.get<SimulationClientDto | ApiResponse<SimulationClientDto>>(`${this.basePath}/clients/${clientId}`);
      
      // O backend retorna o objeto diretamente ou pode estar encapsulado
      if (response.data && typeof response.data === 'object' && 'id' in response.data && !('success' in response.data)) {
        return response.data as SimulationClientDto;
      }
      
      // Se estiver encapsulado em ApiResponse
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as ApiResponse<SimulationClientDto>).data;
        if (!data) {
          throw new Error('Cliente não encontrado');
        }
        return data;
      }
      
      throw new Error('Cliente não encontrado');
    } catch (error: any) {
      console.error('Erro ao buscar detalhes do cliente:', error);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao buscar detalhes do cliente');
    }
  }

  /**
   * Valida números de WhatsApp via Meta API (opcional - não quebra se falhar)
   */
  async validateWhatsApp(phoneNumbers: string[]): Promise<Record<string, boolean>> {
    try {
      const response = await this.api.post<Record<string, boolean> | ApiResponse<Record<string, boolean>>>(
        `${this.basePath}/validate-whatsapp`,
        phoneNumbers
      );
      
      // O backend retorna o objeto diretamente ou pode estar encapsulado
      if (response.data && typeof response.data === 'object' && !('success' in response.data)) {
        return response.data as Record<string, boolean>;
      }
      
      // Se estiver encapsulado em ApiResponse
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        return (response.data as ApiResponse<Record<string, boolean>>).data || {};
      }
      
      return {};
    } catch (error) {
      console.warn('Erro ao validar WhatsApp (não crítico):', error);
      return {}; // Retorna vazio se falhar - não quebra a aplicação
    }
  }

  /**
   * Calcula simulação de planos baseada nas linhas selecionadas
   */
  async calculateSimulation(request: SimulationRequestDto): Promise<SimulationResultDto> {
    try {
      const response = await this.api.post<SimulationResultDto | ApiResponse<SimulationResultDto>>(
        `${this.basePath}/calculate`,
        request
      );
      
      // O backend retorna o objeto diretamente ou pode estar encapsulado
      if (response.data && typeof response.data === 'object' && 'valorTotal' in response.data && !('success' in response.data)) {
        const resultado = response.data as SimulationResultDto;
        return resultado;
      }
      
      // Se estiver encapsulado em ApiResponse
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        const data = (response.data as ApiResponse<SimulationResultDto>).data;
        if (!data) {
          throw new Error('Erro ao calcular simulação - dados não encontrados na resposta');
        }
        return data;
      }
      
      console.error('[SimulationService] Formato de resposta não reconhecido:', response.data);
      throw new Error('Erro ao calcular simulação - formato de resposta inválido');
    } catch (error: any) {
      console.error('[SimulationService] Erro ao calcular simulação:', error);
      console.error('[SimulationService] Response do erro:', error.response);
      console.error('[SimulationService] Data do erro:', error.response?.data);
      throw new Error(error.response?.data?.message || error.message || 'Erro ao calcular simulação');
    }
  }
}

export const simulationService = new SimulationService();
